import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${window.location.origin}/api`;

/**
 * Inline-editable table cell. Double-click to enter edit mode; Enter or blur
 * to save; Escape to cancel. Calls PUT /api/staff/:id with the patched field.
 *
 * Props:
 *   staff   — the staff row object
 *   field   — key to edit (e.g. "total_time", "notes")
 *   onSaved — (updatedStaff) => void; lets the parent refresh its list
 *   render  — (value) => ReactNode for the read-only display
 *   className — wrapper class
 *   placeholder — placeholder for the input
 */
const InlineEditCell = ({ staff, field, onSaved, render, className = "", placeholder = "" }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(staff[field] ?? "");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setValue(staff[field] ?? "");
  }, [staff, field]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const cancel = () => {
    setValue(staff[field] ?? "");
    setEditing(false);
  };

  const save = async () => {
    const trimmed = String(value).trim();
    if (trimmed === (staff[field] ?? "")) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/staff/${staff.id}`, { [field]: trimmed });
      toast.success(`${field.replace("_", " ")} updated`);
      setEditing(false);
      if (onSaved) onSaved(res.data);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(`Save failed: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); save(); }
    else if (e.key === "Escape") { e.preventDefault(); cancel(); }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={value}
        disabled={saving}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={onKey}
        onBlur={save}
        onClick={(e) => e.stopPropagation()}
        placeholder={placeholder}
        data-testid={`inline-edit-${field}-${staff.id}`}
        className={`bg-black border border-[#00E5FF]/50 px-2 py-1 font-mono text-xs text-white focus:outline-none focus:border-[#00E5FF] w-full ${className}`}
      />
    );
  }

  return (
    <button
      type="button"
      onDoubleClick={(e) => { e.stopPropagation(); setEditing(true); }}
      onClick={(e) => e.stopPropagation()}
      title="Double-click to edit"
      data-testid={`inline-view-${field}-${staff.id}`}
      className={`text-left w-full hover:bg-zinc-800/40 transition-colors px-1 py-0.5 ${className}`}
    >
      {render ? render(staff[field]) : (staff[field] || "—")}
    </button>
  );
};

export default InlineEditCell;
